import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

// shadcn base-mira button, resized to the Sao control scale (§3.7: 28 sm,
// 32 default, 36 lg, 40 sign-in) and recoloured to Sao tokens. Focus uses the
// single global outline from src/styles/app.css, never a ring.
const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-md border border-transparent bg-clip-padding font-medium whitespace-nowrap transition-[color,background-color,border-color,opacity,transform] duration-(--dur-fast) select-none active:not-aria-[haspopup]:scale-[0.98] motion-reduce:active:not-aria-[haspopup]:scale-100 disabled:pointer-events-none disabled:opacity-50 aria-busy:cursor-progress aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary-hover',
        brand: 'bg-brand text-brand-foreground hover:bg-brand/90',
        outline:
          'border-border-strong bg-card text-foreground hover:bg-accent hover:text-accent-foreground aria-expanded:bg-accent',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-surface-3 aria-expanded:bg-surface-3',
        ghost:
          'text-foreground hover:bg-accent hover:text-accent-foreground aria-expanded:bg-accent',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        'destructive-outline':
          'border-danger-border bg-card text-danger-ink hover:bg-danger-tint',
        link: 'h-auto px-0 text-foreground underline underline-offset-4 hover:decoration-2',
      },
      size: {
        default:
          'h-8 gap-1.5 px-3 text-base has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5',
        xs: "h-6 gap-1 rounded-sm px-2 text-xs has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 px-2.5 text-sm has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-3.5",
        lg: 'h-9 gap-1.5 px-3.5 text-base has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3',
        xl: 'h-10 gap-2 px-4 text-base',
        icon: 'size-8',
        'icon-xs': "size-6 rounded-sm [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': "size-7 [&_svg:not([class*='size-'])]:size-3.5",
        'icon-lg': "size-9 [&_svg:not([class*='size-'])]:size-[18px]",
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  pending = false,
  disabled,
  type = 'button',
  children,
  ...props
}: ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    /** Shows a spinner and ignores presses until it is false again. */
    pending?: boolean;
  }) {
  return (
    <ButtonPrimitive
      data-slot="button"
      type={type}
      disabled={disabled || pending}
      aria-busy={pending || undefined}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {pending ? <Spinner className="size-3.5" /> : null}
      {children}
    </ButtonPrimitive>
  );
}

export { Button, buttonVariants };
